import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import AppWrapper from '../../../components/AppWrapper';
import EditFooterBar from '../../../components/EditFooterBar';
import PageTitle from '../../../components/PageTitle';
import { breachcrumbPath } from '../../../utils/breadcrumbConst';
import { ActionBarStyled, SelectStyled } from '../../../components/PageAction/styles';
import { FlexboxStyled } from '../../../styles/IndexStyles';
import { BulkReminder } from '../TemplateForms/BulkReminder';
import { PlatformwideNotification } from '../TemplateForms/PlatformwideNotification';
import { SurveyFeedback } from '../TemplateForms/SurveyFeedback';
import { StudyPromotion } from '../TemplateForms/StudyPromotion';
import { Newsletter } from '../TemplateForms/Newsletter';
import { InputWrapper } from '../../../styles/EditDeleteStyles';
import { LabelStyled } from '../../../components/InputText/styles';
import { useBulkEmail } from '../../../hooks/useBulkEmail';
import {
  bulkReminderSchema,
  platformWideNotificationSchema,
  studyPromotionSchema,
  surveyFeedbackSchema,
  newsletterSchema,
} from '../../../utils/validations';
import { showEmailModal } from '../TemplateForms/Shared/emailModal';
import { showConfirmModal } from '../TemplateForms/Shared/confirmSendModel';
import { showAlertModal } from '../TemplateForms/Shared/alertModal';

type TemplateFormProps = {
  onChange: (data: any) => void;
  continueEditing?: any;
  errors?: any;
};

type TemplateOption = {
  type: string | undefined;
  label: string;
  Form?: React.FunctionComponent<TemplateFormProps>;
};

const emailTemplateOptions: TemplateOption[] = [
  { type: undefined, label: 'Select Template' },
  { type: 'bulk_reminder', label: 'Bulk Reminder' },
  {
    type: 'platform_notification',
    label: 'Platform-wide Notification',
  },
  { type: 'survey_feedback', label: 'Survey / Feedback' },
  { type: 'study_promotion', label: 'Study Promotion & Engagement' },
  { type: 'newsletter', label: 'Newsletter' },
];

const BulkEmailEdit = ({ isNewsletterEdit }: { isNewsletterEdit?: boolean }) => {
  const { id: newsletterId } = useParams();
  const location: any = useLocation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<any>({});
  const [toastMessage, setToastMessage] = useState<IToastMessage>();
  const [template, setTemplate] = useState<any>();
  const [continueEditing, setContinueEditing] = useState<any>(null);
  const [errors, setErrors] = useState<any>({});
  const [loading, setLoading] = useState<any>(false);

  const {
    sendTestReminderEmail,
    sendPlatformWideNotification,
    sendStudyPromotionEmail,
    sendSurveyFeedbackEmail,
    sendNewsletterEmailTest,
    updateNewsletterEmail,
    upsertNewsletterEmail,
    removeNewsletterEmail,
    getSignedUrl,
  } = useBulkEmail();

  const emailValidationSchemas: Record<string, any> = {
    bulk_reminder: bulkReminderSchema,
    platform_notification: platformWideNotificationSchema,
    study_promotion: studyPromotionSchema,
    survey_feedback: surveyFeedbackSchema,
    newsletter: newsletterSchema,
  };

  const handleNewsletterSaveAndContinue = async () => {
    const draft = { ...formData, status: 'draft' };
    if (newsletterId) {
      const id = await updateNewsletterEmail(formData);
      setContinueEditing(id);
    } else {
      const id = await upsertNewsletterEmail(draft);
      setContinueEditing(id);
    }
    await showAlertModal('Newsletter saved');
  };

  const handleNewsletterSaveDraft = async () => {
    const draft = { ...formData, status: 'draft' };

    await upsertNewsletterEmail(draft);
    navigate('/admin/bulkEmail');
  };

  const handleNewsletterComplete = async () => {
    const final = { ...formData, status: 'scheduled' };
    const confirmation = await showConfirmModal(
      'schedule',
      'Would you like to schedule the newsletter?'
    );
    if (confirmation) {
      await upsertNewsletterEmail(final);
      navigate('/admin/bulkEmail');
    }
  };

  const handleDeleteNewsletter = async () => {
    await removeNewsletterEmail(newsletterId || continueEditing);
    navigate('/admin/bulkEmail');
  };

  const filterValidImages = (images: any[]): File[] =>
    images.filter(
      (img) =>
        img instanceof File &&
        img.name &&
        img.size > 0 &&
        img.type &&
        typeof img.name === 'string' &&
        typeof img.type === 'string'
    );

  const handleUploadNewsletterImages = async (images: (File | undefined)[]) => {
    if (images.includes(undefined)) {
      showAlertModal('Section image undefined email did not submit');
      throw new Error('section image undefined');
    }
    const validImages = filterValidImages(images);
    for await (const image of validImages) {
      const { signedUrl } = await getSignedUrl(image.name);
      await fetch(signedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': image.type,
        },
        body: image,
      });
    }
  };

  const handleEventNewsletter = async (
    type: string,
    email: string | null | undefined = undefined
  ) => {
    const images = [
      formData.bannerImage,
      ...formData.sections.map((section: any) => section.sectionImage),
    ];
    try {
      if (type !== 'test')
        await emailValidationSchemas.newsletter.validate(formData, {
          abortEarly: false,
          stripUnknown: true,
        });
      setErrors({});
      console.log('hello');
      await handleUploadNewsletterImages(images);
      switch (type) {
        case 'complete':
          handleNewsletterComplete();
          break;
        case 'edit':
          handleNewsletterSaveAndContinue();
          break;
        case 'draft':
          handleNewsletterSaveDraft();
          break;
        case 'delete':
          handleDeleteNewsletter();
          break;
        case 'test':
          sendNewsletterEmailTest({ ...formData, email });
          break;
        default:
          break;
      }
    } catch (error: any) {
      if (error instanceof Yup.ValidationError) {
        const errorIndex: Record<string, string> = error.inner.reduce((acc: any, curr: any) => {
          acc[curr.path] = curr.message;
          return acc;
        }, {});
        setErrors(errorIndex);
      }
    }
  };

  const sendBulkEmailWithImage = async (
    emailData: any,
    sendEmailFunc: (data: any) => Promise<void>
  ) => {
    const { bannerImage, ...restEmailData } = emailData;
    if (bannerImage?.name) {
      const { signedUrl } = await getSignedUrl(bannerImage.name);
      await fetch(signedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': bannerImage.type,
        },
        body: bannerImage,
      });
      await sendEmailFunc(restEmailData);
    } else {
      await showAlertModal('No banner image to upload');
    }
  };

  const emailActions = {
    sendTestEmail: async () => {
      setLoading(true);
      try {
        await emailValidationSchemas[template].validate(formData, {
          abortEarly: false,
          stripUnknown: true,
        });
        setErrors({});
        const email = await showEmailModal();
        if (email) {
          switch (template) {
            case 'bulk_reminder':
              await sendTestReminderEmail({ ...formData, email, test: true });
              break;
            case 'platform_notification':
              await sendBulkEmailWithImage(
                { ...formData, email, test: true },
                sendPlatformWideNotification
              );
              break;
            case 'study_promotion':
              await sendBulkEmailWithImage(
                { ...formData, email, test: true },
                sendStudyPromotionEmail
              );
              break;
            case 'survey_feedback':
              await sendBulkEmailWithImage(
                { ...formData, email, test: true },
                sendSurveyFeedbackEmail
              );
              break;
            case 'newsletter':
              await handleEventNewsletter('test', email);
              break;
            default:
              break;
          }
          await showAlertModal(`email sent to ${email}`);
        }
      } catch (error) {
        setLoading(false);
        if (error instanceof Yup.ValidationError) {
          const errorIndex: Record<string, string> = error.inner.reduce((acc: any, curr: any) => {
            acc[curr.path] = curr.message;
            return acc;
          }, {});
          setErrors(errorIndex);
        }
      }
      setLoading(false);
    },

    ...(template !== 'newsletter' && {
      sendBulkEmail: async () => {
        setLoading(true);
        const confirmation = await showConfirmModal(
          'send',
          'The email will be sent now, are you sure?'
        );
        if (confirmation) {
          try {
            await emailValidationSchemas[template].validate(formData, {
              abortEarly: false,
              stripUnknown: true,
            });
            setErrors({});
            switch (template) {
              case 'bulk_reminder':
                await sendTestReminderEmail(formData);
                break;
              case 'platform_notification':
                await sendBulkEmailWithImage(formData, sendPlatformWideNotification);
                break;
              case 'study_promotion':
                await sendBulkEmailWithImage(formData, sendStudyPromotionEmail);
                break;
              case 'survey_feedback':
                await sendBulkEmailWithImage(formData, sendSurveyFeedbackEmail);
                break;
              default:
                break;
            }
            await showAlertModal('Bulk email has been sent!');
          } catch (error) {
            if (error instanceof Yup.ValidationError) {
              setLoading(false);
              const errorIndex: Record<string, string> = error.inner.reduce(
                (acc: any, curr: any) => {
                  acc[curr.path] = curr.message;
                  return acc;
                },
                {}
              );
              setErrors(errorIndex);
            }
          }
        }
        setLoading(false);
      },
    }),
  };

  useEffect(() => {
    if (location.state?.toastMessage) {
      setToastMessage(location.state.toastMessage);
    }
    if (isNewsletterEdit) {
      setTemplate('newsletter');
    }
  }, [location]);

  return (
    <AppWrapper
      toastMessage={toastMessage}
      breadcrumbs={[
        breachcrumbPath.admin,
        breachcrumbPath.bulkEmail,
        { url: '', name: 'Bulk Email Templates' },
      ]}
    >
      <PageTitle title="Bulk Emails" linkUrl="" buttonText="" showButton={false} />
      <InputWrapper>
        <ActionBarStyled>
          <FlexboxStyled style={{ marginBottom: 0 }}>
            <LabelStyled style={{ color: 'black' }}>Choose a template:</LabelStyled>
            <SelectStyled
              onChange={({ currentTarget }: React.ChangeEvent<HTMLSelectElement>) => {
                setTemplate(currentTarget.value);
                setErrors({});
              }}
              value={template?.type}
            >
              {emailTemplateOptions.map(({ label, type }, i) => (
                <option value={type} key={i}>
                  {label}
                </option>
              ))}
            </SelectStyled>
          </FlexboxStyled>
        </ActionBarStyled>
      </InputWrapper>
      {template === 'bulk_reminder' && (
        <BulkReminder onChange={setFormData} error={errors} loading={loading} />
      )}
      {template === 'platform_notification' && (
        <PlatformwideNotification onChange={setFormData} error={errors} loading={loading} />
      )}
      {template === 'survey_feedback' && (
        <SurveyFeedback onChange={setFormData} error={errors} loading={loading} />
      )}
      {template === 'study_promotion' && (
        <StudyPromotion onChange={setFormData} error={errors} loading={loading} />
      )}
      {template === 'newsletter' && (
        <Newsletter
          onChange={setFormData}
          continueEditing={continueEditing}
          error={errors}
          loading={loading}
        />
      )}
      {!!template && (
        <EditFooterBar
          showNew={false}
          showDelete={false}
          showFunctionalDelete={
            (!!newsletterId && template === 'newsletter') ||
            (continueEditing && template === 'newsletter')
          }
          disableTestEmail={template === 'newsletter' && (newsletterId ? false : !continueEditing)}
          showEdit={template === 'newsletter'}
          showSave={false}
          showSaveDraft={template === 'newsletter'}
          showComplete={template === 'newsletter'}
          handleSubmit={handleEventNewsletter}
          emailActions={emailActions}
          altUrlPath="admin/bulkEmail"
          error={errors}
          loading={loading}
        />
      )}
    </AppWrapper>
  );
};

export default BulkEmailEdit;
